import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { downloadAuthedFile } from '../../lib/fileDownload';
import logger from '../../lib/logger';
import detectLinkProvider from '../../lib/linkProvider';
import {
  FolderOpen, UploadCloud, Download, Trash2, Loader2, Sparkles,
  FileText, FileSpreadsheet, Presentation, Image as ImageIcon, File as FileIcon,
  Link2, ExternalLink, Plus, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACCEPT = '.pdf,.docx,.txt,.md,.markdown,.xlsx,.pptx,.png,.jpg,.jpeg';
const MAX_BYTES = 25 * 1024 * 1024;

function iconForExt(ext) {
  switch (ext) {
    case '.pdf':
    case '.docx':
    case '.txt':
    case '.md':
    case '.markdown':
      return FileText;
    case '.xlsx':
      return FileSpreadsheet;
    case '.pptx':
      return Presentation;
    case '.png':
    case '.jpg':
    case '.jpeg':
      return ImageIcon;
    default:
      return FileIcon;
  }
}

function formatBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

export default function WorkspaceDocumentsTab() {
  const { estimateId } = useOutletContext();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [addingLink, setAddingLink] = useState(false);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/estimates/${estimateId}/source-documents`,
        { withCredentials: true },
      );
      setDocuments(res.data.documents || []);
    } catch (err) {
      logger.error('Failed to load source documents:', err);
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const uploadFiles = useCallback(async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setUploading(true);
    let ok = 0;
    let failed = 0;
    for (const f of list) {
      if (f.size > MAX_BYTES) {
        toast.error(`"${f.name}" exceeds the 25 MB limit`);
        failed += 1;
        continue;
      }
      try {
        const form = new FormData();
        form.append('file', f);
        await axios.post(
          `${API_URL}/api/estimates/${estimateId}/source-documents`,
          form,
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
        );
        ok += 1;
      } catch (err) {
        failed += 1;
        const detail = err?.response?.data?.detail || `Could not upload "${f.name}"`;
        toast.error(detail);
      }
    }
    setUploading(false);
    if (ok > 0) toast.success(`Uploaded ${ok} document${ok > 1 ? 's' : ''}${failed ? ` (${failed} failed)` : ''}`);
    await fetchDocs();
  }, [estimateId, fetchDocs]);

  const onInputChange = (e) => {
    uploadFiles(e.target.files);
    e.target.value = '';
  };

  const addLinkUrl = useCallback(async (rawUrl, title = '') => {
    const url = (rawUrl || '').trim();
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Enter a valid URL starting with http:// or https://');
      return false;
    }
    setAddingLink(true);
    try {
      await axios.post(
        `${API_URL}/api/estimates/${estimateId}/source-documents/links`,
        { url, title: (title || '').trim() || null },
        { withCredentials: true },
      );
      const prov = detectLinkProvider(url);
      toast.success(prov.key !== 'link' ? `${prov.label} link added` : 'Link added');
      await fetchDocs();
      return true;
    } catch (err) {
      logger.error('Add link failed:', err);
      toast.error(err?.response?.data?.detail || 'Could not add the link');
      return false;
    } finally {
      setAddingLink(false);
    }
  }, [estimateId, fetchDocs]);

  const addLink = useCallback(async () => {
    const ok = await addLinkUrl(linkUrl, linkTitle);
    if (ok) { setLinkUrl(''); setLinkTitle(''); setShowLinkForm(false); }
  }, [addLinkUrl, linkUrl, linkTitle]);

  // Paste a URL anywhere on the tab (outside a form field) to add it as a link,
  // as fast as dropping a file.
  useEffect(() => {
    const onPaste = (e) => {
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || t?.isContentEditable) return;
      const text = (e.clipboardData?.getData('text') || '').trim();
      if (/^https?:\/\/\S+$/i.test(text)) {
        e.preventDefault();
        addLinkUrl(text);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [addLinkUrl]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const handleDownload = async (doc) => {
    try {
      await downloadAuthedFile(
        `/api/estimates/${estimateId}/source-documents/${doc.id}/download`,
        doc.filename,
      );
    } catch (err) {
      logger.error('Download failed:', err);
      toast.error('Could not download the document');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"? This cannot be undone.`)) return;
    setDeletingId(doc.id);
    try {
      await axios.delete(
        `${API_URL}/api/estimates/${estimateId}/source-documents/${doc.id}`,
        { withCredentials: true },
      );
      toast.success('Document deleted');
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err) {
      logger.error('Delete failed:', err);
      toast.error('Could not delete the document');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAnalyze = async (doc) => {
    setAnalyzingId(doc.id);
    try {
      await axios.post(
        `${API_URL}/api/estimates/${estimateId}/source-documents/${doc.id}/analyze`,
        {},
        { withCredentials: true },
      );
      toast.success('Analyzing… we\'ll surface the extracted requirements on the Requirements tab when ready.', {
        duration: 8000,
        action: {
          label: 'Open Requirements',
          onClick: () => navigate(`/estimator/${estimateId}/requirements`),
        },
      });
    } catch (err) {
      logger.error('Analyze failed:', err);
      const detail = err?.response?.data?.detail || 'Could not start analysis';
      toast.error(detail);
    } finally {
      setAnalyzingId(null);
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="workspace-documents">
      <div className="flex items-center gap-2 mb-5">
        <FolderOpen className="w-4 h-4 text-slate-400" />
        <h2 className="text-sm font-semibold text-[#0F2040]">Source Documents</h2>
        {documents.length > 0 ? (
          <Badge variant="secondary" className="text-[9px]" data-testid="source-docs-breakdown">
            {(() => {
              const files = documents.filter((d) => d.kind !== 'link').length;
              const links = documents.length - files;
              const parts = [];
              if (files) parts.push(`${files} file${files === 1 ? '' : 's'}`);
              if (links) parts.push(`${links} link${links === 1 ? '' : 's'}`);
              return parts.join(' · ');
            })()}
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-[9px]">0</Badge>
        )}
      </div>

      {/* Upload dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed transition-colors p-8 text-center mb-6 ${
          dragActive
            ? 'border-[#4ABFCF] bg-[#4ABFCF]/5'
            : 'border-slate-300 hover:border-[#4ABFCF] hover:bg-slate-50'
        }`}
        data-testid="documents-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={onInputChange}
          className="hidden"
          data-testid="documents-file-input"
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
            <p className="text-sm">Uploading…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <UploadCloud className="w-7 h-7 text-[#4ABFCF]" />
            <p className="text-sm font-medium text-[#0F2040]">
              Drop files here, click to browse, or paste a link
            </p>
            <p className="text-[11px] text-slate-400">
              PDF, DOCX, TXT, MD, XLSX, PPTX, PNG, JPG · up to 25 MB each — or paste a URL to add it as a link
            </p>
          </div>
        )}
      </div>

      {/* Add-a-link affordance */}
      <div className="mb-6" data-testid="add-link-section">
        {!showLinkForm ? (
          <button
            type="button"
            onClick={() => setShowLinkForm(true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4ABFCF] hover:text-[#0F2040] transition-colors"
            data-testid="add-link-toggle"
          >
            <Plus className="w-3.5 h-3.5" />
            Add a document link
          </button>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="add-link-form">
            <div className="flex items-center gap-2 mb-2">
              <Link2 className="w-4 h-4 text-[#4ABFCF]" />
              <span className="text-sm font-semibold text-[#0F2040]">Add a document link</span>
              <button
                type="button"
                onClick={() => { setShowLinkForm(false); setLinkUrl(''); setLinkTitle(''); }}
                className="ml-auto p-1 rounded text-slate-400 hover:text-slate-600"
                data-testid="add-link-cancel"
                title="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://drive.google.com/…"
                className="sm:flex-[2] bg-white"
                data-testid="add-link-url-input"
                onKeyDown={(e) => { if (e.key === 'Enter') addLink(); }}
              />
              <Input
                value={linkTitle}
                onChange={(e) => setLinkTitle(e.target.value)}
                placeholder="Title (optional)"
                className="sm:flex-1 bg-white"
                data-testid="add-link-title-input"
                onKeyDown={(e) => { if (e.key === 'Enter') addLink(); }}
              />
              <Button
                onClick={addLink}
                disabled={addingLink || !linkUrl.trim()}
                className="bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white shrink-0"
                data-testid="add-link-submit"
              >
                {addingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add link'}
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">
              Link to a doc hosted elsewhere (Google Drive, SharePoint, Confluence…). Links open in a new tab and aren&apos;t analyzed by AI.
            </p>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
        </div>
      ) : documents.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <FolderOpen className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              No source documents yet. Upload the spec docs, RFPs, or notes — or add a link — that informed this estimate.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="documents-list">
          {documents.map((doc) => {
            const isLink = doc.kind === 'link';
            const provider = isLink ? detectLinkProvider(doc.url) : null;
            const IconComp = isLink ? provider.Icon : iconForExt(doc.ext);
            const isAnalyzing = analyzingId === doc.id;
            const isDeleting = deletingId === doc.id;
            let hostname = '';
            if (isLink) {
              try { hostname = new URL(doc.url).hostname.replace(/^www\./, ''); } catch { hostname = doc.url; }
            }
            return (
              <div
                key={doc.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition-colors"
                data-testid={`document-row-${doc.id}`}
              >
                <div className={`w-9 h-9 rounded-md border flex items-center justify-center shrink-0 ${isLink ? 'bg-[#4ABFCF]/10 border-[#4ABFCF]/30' : 'bg-slate-50 border-slate-200'}`}>
                  <IconComp className={`w-4 h-4 ${isLink ? provider.className : 'text-[#4ABFCF]'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  {isLink ? (
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-[#0F2040] truncate hover:text-[#4ABFCF] hover:underline inline-flex items-center gap-1 max-w-full"
                      title={doc.url}
                      data-testid={`document-link-${doc.id}`}
                    >
                      <span className="truncate">{doc.filename}</span>
                      <ExternalLink className="w-3 h-3 shrink-0 text-slate-400" />
                    </a>
                  ) : (
                    <p className="text-sm font-medium text-[#0F2040] truncate" title={doc.filename}>
                      {doc.filename}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                    {isLink ? (
                      <span
                        className={`inline-flex items-center gap-1 font-medium ${provider.className}`}
                        data-testid={`document-link-provider-${doc.id}`}
                      >
                        <provider.Icon className="w-2.5 h-2.5" />
                        {provider.label}
                        {provider.key !== 'link' && <span className="text-slate-300 font-normal">· {hostname}</span>}
                      </span>
                    ) : (
                      <span>{formatBytes(doc.size_bytes)}</span>
                    )}
                    <span className="text-slate-300">·</span>
                    <span>{doc.uploaded_by}</span>
                    <span className="text-slate-300">·</span>
                    <span>{formatDate(doc.uploaded_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {isLink ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}
                      className="h-8 gap-1 text-[#4ABFCF] hover:text-[#0F2040] hover:bg-[#4ABFCF]/10"
                      data-testid={`document-open-${doc.id}`}
                      title="Open link in a new tab"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Open
                    </Button>
                  ) : (
                    <>
                      {doc.analyzable && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAnalyze(doc)}
                          disabled={isAnalyzing}
                          className="h-8 gap-1 text-[#4ABFCF] hover:text-[#0F2040] hover:bg-[#4ABFCF]/10"
                          data-testid={`document-analyze-${doc.id}`}
                          title="Extract requirements from this document with AI"
                        >
                          {isAnalyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                          Analyze
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownload(doc)}
                        className="h-8 w-8 text-slate-400 hover:text-[#0F2040]"
                        data-testid={`document-download-${doc.id}`}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(doc)}
                    disabled={isDeleting}
                    className="h-8 w-8 text-slate-400 hover:text-rose-600"
                    data-testid={`document-delete-${doc.id}`}
                    title="Delete"
                  >
                    {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
